import { zodResolver } from "@hookform/resolvers/zod";
import { API } from "@swivl-mono/api-helper";
import { PhoneNumberHelper, filterClasses } from "@swivl-mono/helpers";
import { FeatureFlagKey, NotificationChannel, NotificationGroup, RoutingGroupInterface } from "@swivl-mono/types";
import { Checkbox, ChipSelect, FormFieldWidth, HelperText, Input, PhoneInput, SelectOutlet } from "@swivl-mono/ui";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { BiMobileAlt } from "react-icons/bi";
import { HiOutlineMail } from "react-icons/hi";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { TbBrandSlack, TbDeviceDesktop } from "react-icons/tb";
import { z } from "zod";

import { SavedIndicator, useSavedIndicator } from "../../../../../Components/SavedIndicator/SavedIndicator";
import { Tooltip } from "../../../../../Components/Tooltip/Tooltip";
import { isFeatureEnabled } from "../../../../../Helpers/FeatureFlags/isFeatureEnabled";
import AccountModel from "../../../../../Models/Account/AccountModel";
import { Member } from "../../../../../Models/Member/Member.Current";
import MembersModel from "../../../../../Models/Member/Members.Model";
import { AlertsModel } from "../../../../../Models/Notifications/Alerts.Model";
import PushNotificationsModel, { PushStatus } from "../../../../../Models/PushNotifications/PushNotifications.Model";

type Channel = "desktop" | "sms" | "email" | "slack";

interface ChannelFlags {
  desktop: boolean;
  sms: boolean;
  email: boolean;
  slack: boolean;
}

interface NotificationPreferences {
  conversations: ChannelFlags;
  ticketAssigned: ChannelFlags;
  ticketNotes: ChannelFlags;
  ticketGroup: ChannelFlags;
  ticketSubscribed: ChannelFlags;
}

type NotificationGroupKey = keyof NotificationPreferences;

interface DestinationFormData {
  emailAddress: string;
  smsNumber: string;
}

const destinationSchema = z.object({
  emailAddress: z.string().refine((val) => !val || z.email().safeParse(val).success, { message: "Enter a valid email address" }),
  smsNumber: z.string().refine((val) => !val || !!PhoneNumberHelper.isPhoneNumberValid(val), { message: "Enter a valid phone number" })
});

const DEFAULT_CHANNEL_FLAGS: ChannelFlags = { desktop: false, sms: false, email: false, slack: false };

const DEFAULT_PREFERENCES: NotificationPreferences = {
  conversations: DEFAULT_CHANNEL_FLAGS,
  ticketAssigned: { ...DEFAULT_CHANNEL_FLAGS, email: true },
  ticketNotes: { ...DEFAULT_CHANNEL_FLAGS, email: true },
  ticketGroup: { ...DEFAULT_CHANNEL_FLAGS, email: true },
  ticketSubscribed: { ...DEFAULT_CHANNEL_FLAGS, email: true }
};

const ROW_CHANNELS: { key: Channel; label: string }[] = [
  { key: "desktop", label: "Desktop" },
  { key: "sms", label: "Mobile" },
  { key: "email", label: "Email" }
];

interface TestButtonProps {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

function TestButton({ icon, label, onClick, loading = false, disabled = false }: TestButtonProps): ReactNode {
  const isDisabled = loading || disabled;
  return (
    <button
      className={filterClasses(
        "flex h-[26px] items-center gap-2 rounded border px-3 transition-colors",
        isDisabled ? "cursor-not-allowed border-grey-300 bg-grey-300 text-grey-500" : "cursor-pointer border-grey-300 bg-white hover:bg-grey-50"
      )}
      onClick={onClick}
      disabled={isDisabled}
    >
      {icon}
      <span className="body-sm-alt whitespace-nowrap">{label}</span>
    </button>
  );
}

interface NotifRowProps {
  label: string;
  groupKey: NotificationGroupKey;
  channelValues: ChannelFlags;
  disabledChannels?: Channel[];
  onToggle: (group: NotificationGroupKey, channel: Channel, checked: boolean) => void;
  isLast?: boolean;
}

function NotifRow({ label, groupKey, channelValues, disabledChannels = [], onToggle, isLast }: NotifRowProps): ReactNode {
  return (
    <div className={filterClasses("flex items-center justify-between px-4 py-3", !isLast && "border-b border-grey-200")}>
      <span className="body-sm text-black">{label}</span>
      <div className="flex gap-4">
        {ROW_CHANNELS.map(({ key, label: channelLabel }) => (
          <Checkbox
            key={key}
            name={`${groupKey}-${key}`}
            label={channelLabel}
            checked={channelValues[key]}
            disabled={disabledChannels.includes(key)}
            onCheckedChange={(checked) => onToggle(groupKey, key, checked === true)}
            classNames={{ label: "body-sm" }}
          />
        ))}
      </div>
    </div>
  );
}

const CHANNEL_TO_ENUM: Record<Channel, NotificationChannel> = {
  desktop: NotificationChannel.DESKTOP,
  sms: NotificationChannel.SMS,
  email: NotificationChannel.EMAIL,
  slack: NotificationChannel.SLACK
};

export function NotificationsTab(): ReactNode {
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_PREFERENCES);
  const preferencesRef = useRef<NotificationPreferences>(preferences);
  preferencesRef.current = preferences;
  const [testLoading, setTestLoading] = useState<Channel | null>(null);
  const [routingGroups, setRoutingGroups] = useState<RoutingGroupInterface[]>([]);
  const [autoSubscribeSlugs, setAutoSubscribeSlugs] = useState<string[]>([]);
  const autoSubscribeSlugsRef = useRef<string[]>([]);
  const pushStatus = PushNotificationsModel.useState((s) => s.pushStatus);
  const { account } = AccountModel.useState();
  const saved = useSavedIndicator();

  const orgId = Member.orgId;
  const memberId = Member.id!;
  const groupsEnabled = isFeatureEnabled(FeatureFlagKey.TicketingGroups);

  const destinationForm = useForm<DestinationFormData>({
    mode: "onBlur",
    resolver: zodResolver(destinationSchema),
    defaultValues: {
      emailAddress: account?.email ?? "",
      smsNumber: ""
    }
  });

  const [loadedSmsNumber, setLoadedSmsNumber] = useState<string | undefined>(undefined);
  const smsNumber = destinationForm.watch("smsNumber");
  const disabledChannels: Channel[] = !smsNumber?.trim() ? ["sms"] : [];

  useEffect(() => {
    PushNotificationsModel.refresh();
  }, []);

  useEffect(() => {
    async function loadPreferences(): Promise<void> {
      try {
        const [result, groups] = await Promise.all([
          API.NotificationPreferences.get(orgId, memberId),
          API.RoutingGroup.listGroupsForNotificationPreferences(orgId)
        ]);
        if (result.preferences) {
          const raw = result.preferences as unknown as Record<string, ChannelFlags>;
          setPreferences({
            conversations: raw.conversations ?? DEFAULT_CHANNEL_FLAGS,
            ticketAssigned: raw.ticketAssigned ?? DEFAULT_PREFERENCES.ticketAssigned,
            ticketNotes: raw.ticketNotes ?? DEFAULT_PREFERENCES.ticketNotes,
            ticketGroup: raw.ticketGroup ?? DEFAULT_PREFERENCES.ticketGroup,
            ticketSubscribed: raw.ticketSubscribed ?? DEFAULT_PREFERENCES.ticketSubscribed
          });
        }
        const slugs = result.autoSubscribeGroupSlugs ?? [];
        setAutoSubscribeSlugs(slugs);
        autoSubscribeSlugsRef.current = slugs;
        setRoutingGroups(groups.filter((g) => !g.isArchived && g.isEnabled !== false));
        destinationForm.reset({
          emailAddress: result.notificationEmail || account?.email || "",
          smsNumber: result.notificationPhone ?? ""
        });
        setLoadedSmsNumber(result.notificationPhone ?? "");
      } catch {
        // Use defaults on error
      }
    }
    loadPreferences();
  }, [orgId, memberId]);

  const persistPreferences = useCallback(
    async (updated: NotificationPreferences): Promise<boolean> => {
      const fullPreferences = {
        [NotificationGroup.CONVERSATIONS]: updated.conversations,
        [NotificationGroup.TICKET_ASSIGNED]: updated.ticketAssigned,
        [NotificationGroup.TICKET_NOTES]: updated.ticketNotes,
        [NotificationGroup.TICKET_GROUP]: updated.ticketGroup,
        [NotificationGroup.TICKET_SUBSCRIBED]: updated.ticketSubscribed
      };
      try {
        await API.NotificationPreferences.update(orgId, memberId, { preferences: fullPreferences });
        PushNotificationsModel.setState({ conversationsDesktopEnabled: updated.conversations.desktop });
        saved.flash();
        return true;
      } catch {
        return false;
      }
    },
    [orgId, memberId, saved]
  );

  const lastAutoUncheckAttempt = useRef<string>("");
  useEffect(() => {
    if (smsNumber?.trim()) {
      lastAutoUncheckAttempt.current = "";
      return;
    }
    const hasSms = Object.values(preferences).some((flags) => flags.sms);
    if (!hasSms) {
      lastAutoUncheckAttempt.current = "";
      return;
    }
    const stateKey = Object.values(preferences)
      .map((f) => f.sms)
      .join("-");
    if (lastAutoUncheckAttempt.current === stateKey) return;
    lastAutoUncheckAttempt.current = stateKey;

    const rollback = preferences;
    const updated = Object.fromEntries(Object.entries(preferences).map(([k, v]) => [k, { ...v, sms: false }])) as NotificationPreferences;
    setPreferences(updated);
    void (async () => {
      const persisted = await persistPreferences(updated);
      if (!persisted) setPreferences(rollback);
    })();
  }, [smsNumber, preferences, persistPreferences]);

  const lastDeniedAutoUncheckAttempt = useRef<string>("");
  useEffect(() => {
    if (pushStatus !== PushStatus.DENIED) {
      lastDeniedAutoUncheckAttempt.current = "";
      return;
    }
    const hasDesktop = Object.values(preferences).some((flags) => flags.desktop);
    if (!hasDesktop) {
      lastDeniedAutoUncheckAttempt.current = "";
      return;
    }
    const stateKey = Object.values(preferences)
      .map((f) => f.desktop)
      .join("-");
    if (lastDeniedAutoUncheckAttempt.current === stateKey) return;
    lastDeniedAutoUncheckAttempt.current = stateKey;

    const rollback = preferences;
    const updated = Object.fromEntries(Object.entries(preferences).map(([k, v]) => [k, { ...v, desktop: false }])) as NotificationPreferences;
    setPreferences(updated);
    void (async () => {
      const persisted = await persistPreferences(updated);
      if (!persisted) setPreferences(rollback);
    })();
  }, [pushStatus, preferences, persistPreferences]);

  const handleToggle = useCallback(
    async (group: NotificationGroupKey, channel: Channel, checked: boolean): Promise<void> => {
      if (channel === "desktop" && checked && pushStatus === PushStatus.DENIED) {
        AlertsModel.errorToast("Desktop notifications are blocked in your browser. Update your browser's site settings to allow notifications.");
        return;
      }

      const current = preferencesRef.current;
      const updated: NotificationPreferences = { ...current, [group]: { ...current[group], [channel]: checked } };
      setPreferences(updated);

      const revert = (): void => {
        setPreferences((prev) => ({ ...prev, [group]: { ...prev[group], [channel]: !checked } }));
      };

      if (channel === "desktop" && checked && pushStatus !== PushStatus.ENABLED_AND_REGISTERED) {
        try {
          await MembersModel.subscribeToPushNotifications();
        } catch {
          revert();
          await PushNotificationsModel.refresh();
          AlertsModel.errorToast("Couldn't enable desktop notifications. Allow notifications in your browser to use this.");
          return;
        }
        await PushNotificationsModel.refresh();
        if (PushNotificationsModel.state.pushStatus !== PushStatus.ENABLED_AND_REGISTERED) {
          revert();
          AlertsModel.errorToast("Couldn't enable desktop notifications. Please try again.");
          return;
        }
      }

      const persisted = await persistPreferences(updated);
      if (!persisted) {
        revert();
        AlertsModel.errorToast("Couldn't save your notification preferences. Please try again.");
        return;
      }

      if (channel === "desktop" && !checked) {
        const anyDesktopOn = Object.values(updated).some((flags) => flags.desktop);
        if (!anyDesktopOn) {
          await MembersModel.unsubscribeFromPushNotifications();
          await PushNotificationsModel.refresh();
        }
      }
    },
    [persistPreferences, pushStatus]
  );

  async function handleDestinationBlur(): Promise<void> {
    const isValid = await destinationForm.trigger();
    if (!isValid) return;
    const values = destinationForm.getValues();
    try {
      await API.NotificationPreferences.update(orgId, memberId, {
        notificationEmail: values.emailAddress,
        notificationPhone: values.smsNumber
      });
      saved.flash();
    } catch {
      // Silently fail
    }
  }

  // The gate's answer, in a sentence (ENG-3373): sent, simulated (why), blocked, or nowhere to send.
  async function handleTestNotification(channel: Channel): Promise<void> {
    setTestLoading(channel);
    try {
      const result = await API.NotificationPreferences.sendTest(orgId, memberId, CHANNEL_TO_ENUM[channel]);
      const label = channel === "sms" ? "text" : channel === "desktop" ? "push" : channel;
      const to = result.destination ? ` to ${result.destination}` : "";
      const why = result.reason ? ` (${result.reason.replace(/_/g, " ")})` : "";
      if (result.outcome === "sent") AlertsModel.successToast(`Test ${label} sent${to}`);
      else if (result.outcome === "simulated") AlertsModel.errorToast(`Test ${label} simulated${why} — nothing was sent`);
      else if (result.outcome === "blocked") AlertsModel.errorToast(`Test ${label} blocked — you have opted out`);
      else if (result.outcome === "no_destination") AlertsModel.errorToast(`No ${label} destination set${why}`);
      else AlertsModel.errorToast(`Test ${label} could not be sent${why}`);
    } catch {
      AlertsModel.errorToast("The test request failed");
    } finally {
      setTestLoading(null);
    }
  }

  async function handleAutoSubscribeAccept(): Promise<void> {
    const slugs = autoSubscribeSlugsRef.current;
    try {
      await API.NotificationPreferences.update(orgId, memberId, { autoSubscribeGroupSlugs: slugs });
      saved.flash();
    } catch {
      // Silently fail
    }
  }

  const anyDesktopOn = Object.values(preferences).some((f) => f.desktop);

  return (
    <div className="flex flex-col gap-6">
      {/* Test Notifications */}
      <div className="flex flex-col gap-2 rounded bg-grey-50 px-4 py-3">
        <div className="flex flex-col">
          <span className="body-sm-alt">Test Notifications</span>
          <span className="body-sm text-grey-600">Preview what each channel looks like</span>
        </div>
        <div className="flex gap-2">
          <TestButton
            icon={<TbDeviceDesktop className="size-3.5" />}
            label="Desktop"
            onClick={() => handleTestNotification("desktop")}
            loading={testLoading === "desktop"}
            disabled={!anyDesktopOn}
          />
          <TestButton
            icon={<BiMobileAlt className="size-3.5" />}
            label="Mobile"
            onClick={() => handleTestNotification("sms")}
            loading={testLoading === "sms"}
            disabled={!smsNumber?.trim()}
          />
          <TestButton
            icon={<HiOutlineMail className="size-3.5" />}
            label="Email"
            onClick={() => handleTestNotification("email")}
            loading={testLoading === "email"}
            disabled={false}
          />
        </div>
      </div>

      {/* Destination Fields */}
      <div className="flex gap-4">
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center gap-1">
            <span className="body-sm-alt text-grey-600">Send Email Notifications to</span>
            <Tooltip
              content="Email address where notifications will be sent"
              placement="bottom"
              classNames={{ tooltip: "text-center body-sm !max-w-64" }}
            >
              <IoMdInformationCircleOutline className="size-4 text-grey-600" />
            </Tooltip>
          </div>
          <Input
            placeholder="Input"
            error={destinationForm.formState.errors.emailAddress?.message}
            {...destinationForm.register("emailAddress", { onBlur: handleDestinationBlur })}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center gap-1">
            <span className="body-sm-alt text-grey-600">Send SMS Notifications to</span>
            <Tooltip
              content="Phone number where SMS notifications will be sent"
              placement="bottom"
              classNames={{ tooltip: "text-center body-sm !max-w-64" }}
            >
              <IoMdInformationCircleOutline className="size-4 text-grey-600" />
            </Tooltip>
          </div>
          <PhoneInput
            placeholder="Input"
            error={destinationForm.formState.errors.smsNumber?.message}
            defaultValue={loadedSmsNumber}
            onChange={(fullNumber) => destinationForm.setValue("smsNumber", fullNumber, { shouldDirty: true, shouldTouch: true })}
            onBlur={handleDestinationBlur}
          />
        </div>
      </div>

      {/* Preferences */}
      <div className="flex flex-col gap-4">
        {pushStatus === PushStatus.DENIED && (
          <div className="rounded border border-orange-500 bg-orange-50 px-4 py-3 text-sm text-black">
            Desktop notifications are blocked in your browser. Update your browser&apos;s site settings for swivlStudio to allow notifications, then
            refresh.
          </div>
        )}

        {/* Contact Center */}
        <div className="rounded border border-grey-200">
          <div className="flex items-center gap-2 border-b border-grey-200 px-4 py-3">
            <span className="heading-base-alt">Contact Center</span>
            <SavedIndicator visible={saved.show} />
          </div>
          <NotifRow
            label="Conversations escalated to you"
            groupKey="conversations"
            channelValues={preferences.conversations}
            disabledChannels={disabledChannels}
            onToggle={handleToggle}
            isLast
          />
        </div>

        {/* Tickets */}
        <div className="rounded border border-grey-200">
          <div className="border-b border-grey-200 px-4 py-3">
            <span className="heading-base-alt">Tickets</span>
          </div>
          <NotifRow
            label="Tickets assigned to you"
            groupKey="ticketAssigned"
            channelValues={preferences.ticketAssigned}
            disabledChannels={disabledChannels}
            onToggle={handleToggle}
          />
          <NotifRow
            label="Comments on tickets assigned to you"
            groupKey="ticketNotes"
            channelValues={preferences.ticketNotes}
            disabledChannels={disabledChannels}
            onToggle={handleToggle}
          />
          {groupsEnabled && (
            <NotifRow
              label="Tickets assigned to a group you are a member of"
              groupKey="ticketGroup"
              channelValues={preferences.ticketGroup}
              disabledChannels={disabledChannels}
              onToggle={handleToggle}
            />
          )}
          <NotifRow
            label="Activity on tickets you are subscribed to"
            groupKey="ticketSubscribed"
            channelValues={preferences.ticketSubscribed}
            disabledChannels={disabledChannels}
            onToggle={handleToggle}
            isLast
          />
          {groupsEnabled && routingGroups.length > 0 && (
            <div className="flex items-center gap-4 border-t border-grey-200 px-4 py-3">
              <span className="body-sm flex-1 whitespace-nowrap text-black">Auto-subscribe to ticket groups</span>
              <ChipSelect
                width={FormFieldWidth.RESPONSIVE_240}
                styles={{ [SelectOutlet.POPOVER]: { minWidth: 220 } }}
                options={routingGroups.map((g) => ({ label: g.name, value: g.slug }))}
                selectedValues={autoSubscribeSlugs}
                onChange={(vals) => {
                  setAutoSubscribeSlugs(vals);
                  autoSubscribeSlugsRef.current = vals;
                }}
                onAccept={handleAutoSubscribeAccept}
              />
            </div>
          )}
        </div>

        <HelperText>
          For a step-by-step guide, follow our{" "}
          <a
            href="https://learn.tryswivl.com/turn-on-push-notifications-in-swivlstudio?utm=swivlStudio"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-black underline hover:opacity-80"
          >
            guide
          </a>
          .
        </HelperText>
      </div>
    </div>
  );
}
